import ColorPalette from '../components/ColorPalette';
import { useState, useEffect, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api, useAuth } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { toast } from "@/lib/toast";
import { formatMoney } from "@/lib/currency";
import { Edit2, Trash2, Loader2, Plus, ArrowRight, X as XIcon, Clock, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";

/* ─── Constants ─── */
const CHART_COLORS = ["#396BE9","#62BF92","#F59E0B","#EF4444","#8B5CF6","#06B6D4","#EC4899","#84CC16","#6B7280"];
const EMOJI_OPTIONS = ["🍔","🚕","🏠","🛒","💊","🎬","📚","⚡","📦","✈️","☕","🐾","🎮","👕","💸","💼","🐶","🎁","🎨","🔧"];
const MONTH_NAMES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DEFAULT_CAT_EMOJI = {"Alimentación":"🍔","Transporte":"🚕","Entretenimiento":"🎬","Salud":"💊","Educación":"📚","Servicios":"⚡","Ropa":"👕","Hogar":"🏠","Otros":"📦","Deudas":"💸","Ahorros":"🐷","Tarjetas de Crédito":"💳","Viajes":"✈️","Mascotas":"🐾","Café":"☕","Tecnología":"🎮","Regalos":"🎁"};

function getCatIconUrl(name) {
  const norm = (name || "").toLowerCase().trim();
  if (norm.includes("aliment") || norm.includes("comida")) return "/Iconos/Categorias/Alimnetacion.png";
  if (norm.includes("transporte") || norm.includes("carro") || norm.includes("moto") || norm.includes("uber")) return "/Iconos/Categorias/transporte.png";
  if (norm.includes("entretenimiento") || norm.includes("diversion") || norm.includes("cine")) return "/Iconos/Categorias/Entretenimiento.png";
  if (norm.includes("salud") || norm.includes("medica") || norm.includes("farmacia")) return "/Iconos/Categorias/salud.png";
  if (norm.includes("hogar") || norm.includes("casa") || norm.includes("muebles")) return "/Iconos/Categorias/Hogar.png";
  if (norm.includes("servicios") || norm.includes("luz") || norm.includes("agua") || norm.includes("gas")) return "/Iconos/Categorias/Servicios.png";
  if (norm.includes("suscri") || norm.includes("netflix") || norm.includes("spotify")) return "/Iconos/Categorias/Suscripciones.png";
  if (norm.includes("viaje") || norm.includes("vuelo") || norm.includes("hotel")) return "/Iconos/Categorias/Viajes.png";
  if (norm.includes("deuda") || norm.includes("prestam") || norm.includes("banco")) return "/Iconos/Categorias/Deudas.png";
  if (norm.includes("ahorro") || norm.includes("meta") || norm.includes("piggy")) return "/Iconos/Categorias/Ahorros.png";
  if (norm.includes("deporte") || norm.includes("gym") || norm.includes("ejercicio") || norm.includes("futbol") || norm.includes("entreno")) return "/Iconos/Categorias/deporte.png";
  return "/Iconos/Categorias/otros.png";
}

function getCatIcon(icon, name="", size=24) {
  if (icon && typeof icon === "string" && (icon.startsWith("/") || icon.includes(".png"))) {
    return <img src={icon} alt={name || "category"} style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  const target = name || icon || "";
  if (target && typeof target === "string" && (target.startsWith("/") || target.includes(".png"))) {
    return <img src={target} alt={name || "category"} style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  const url = getCatIconUrl(target);
  return <img src={url} alt={target} style={{ width: size, height: size, objectFit: "contain" }} />;
}

function getServiceIcon(name, type, icon) {
  // 1. If icon is a path (PNG from icon selector), render it
  if (icon && typeof icon === "string" && icon.startsWith("/")) {
    return <img src={icon} alt={name} style={{ width: 36, height: 36, objectFit: "contain" }} />;
  }
  // 2. If icon is a short emoji, render it
  if (icon && typeof icon === "string" && icon.length <= 4 && icon !== "repeat" && icon !== "credit-card") {
    return <span style={{ fontSize: 26 }}>{icon}</span>;
  }
  // 3. Name-based matching fallback
  const normalized = (name || "").toLowerCase();
  if (normalized.includes("netflix")) {
    return (
      <span style={{
        color: "#E50914",
        fontFamily: "'Manrope', sans-serif",
        fontWeight: "900",
        fontSize: "28px"
      }}>
        N
      </span>
    );
  }
  if (normalized.includes("spotify")) {
    return (
      <svg width="34" height="34" viewBox="0 0 24 24" fill="#1DB954">
        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424c-.18.295-.565.387-.86.207-2.377-1.454-5.37-1.783-8.893-.982-.336.075-.668-.135-.744-.47-.077-.337.135-.668.47-.745 3.856-.88 7.15-.5 9.822 1.135.296.18.387.563.205.855zm1.224-2.724c-.226.367-.707.487-1.074.26-2.72-1.672-6.87-2.157-10.076-1.182-.413.125-.847-.107-.972-.52-.125-.413.108-.847.52-.973 3.666-1.112 8.24-.57 11.34 1.342.368.228.488.708.262 1.073zm.106-2.833C14.492 8.76 8.707 8.567 5.367 9.58c-.524.16-1.077-.14-1.237-.665-.16-.525.14-1.078.665-1.238 3.83-1.162 10.222-.94 14.185 1.412.473.28.627.893.346 1.365-.28.472-.893.626-1.365.346z"/>
      </svg>
    );
  }
  // 4. Generic fallbacks
  if (type === "subscription") return "🔄";
  if (type === "credit_card") return "💳";
  return "⚡";
}

function formatUpcomingDate(dateObj) {
  if (!dateObj || isNaN(dateObj.getTime())) return "";
  let formatted = dateObj.toLocaleDateString("es-CO", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });
  // Remove commas
  formatted = formatted.replace(/,/g, "");
  // Capitalize first letter
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

function fmt(amount, currency="COP") {
  return formatMoney(amount, currency);
}

function fmtCompact(amount, currency="COP") {
  return formatMoney(amount, currency, { style: "decimal" });
}

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function fmtDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("es-CO", { day:"numeric", month:"short", timeZone:"America/Bogota" });
}

function fmtKPI(amount, currency="COP") {
  if (Math.abs(amount) >= 1000000000000) {
    return new Intl.NumberFormat("es-CO", { style:"currency", currency, minimumFractionDigits:0, maximumFractionDigits:1 }).format(amount / 1000000000000) + "T";
  }
  if (Math.abs(amount) >= 1000000000) {
    return new Intl.NumberFormat("es-CO", { style:"currency", currency, minimumFractionDigits:0, maximumFractionDigits:1 }).format(amount / 1000000000) + "B";
  }
  if (Math.abs(amount) >= 10000000) {
    return new Intl.NumberFormat("es-CO", { style:"currency", currency, minimumFractionDigits:0, maximumFractionDigits:1 }).format(amount / 1000000) + "M";
  }
  return fmt(amount, currency);
}

/* ─── Styles ─── */
const card = {
  background: "#FFFFFF",
  borderRadius: 20,
  boxShadow: "-2px 0px 20px rgba(176,179,189,0.25)",
  padding: 24,
};

const sectionTitle = {
  fontFamily: "'Manrope', sans-serif",
  fontWeight: 700,
  fontSize: 22,
  color: "#255E99",
  margin: 0,
};

/* ─── Empty State ─── */
function EmptyState({ title = "Sin resultados", subtitle = "" }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"20px 0", gap:12, width:"100%" }}>
      {/* SVG Illustration */}
      <svg width="150" height="120" viewBox="0 0 250 200" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Background blob */}
        <ellipse cx="125" cy="100" rx="83" ry="35" fill="#F3F7FF" />
        {/* Magnifying glass circle */}
        <circle cx="108" cy="85" r="33" stroke="#1F64E7" strokeWidth="4" fill="none" />
        <circle cx="108" cy="85" r="23" stroke="#1F64E7" strokeWidth="4" fill="#FFFFFF" />
        <ellipse cx="102" cy="80" rx="8" ry="7" fill="#E8F0FE" />
        {/* Handle */}
        <line x1="132" y1="110" x2="148" y2="130" stroke="#1F64E7" strokeWidth="4" strokeLinecap="round" />
        {/* Handle grip */}
        <rect x="144" y="124" width="18" height="14" rx="4" fill="#E8F0FE" stroke="#1F64E7" strokeWidth="4" transform="rotate(-45 153 131)" />
        {/* Decorative lines - left */}
        <line x1="30" y1="90" x2="60" y2="90" stroke="#75A4FE" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 6" />
        <line x1="40" y1="105" x2="62" y2="105" stroke="#75A4FE" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 6" />
        {/* Decorative lines - right */}
        <line x1="165" y1="75" x2="195" y2="75" stroke="#75A4FE" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 6" />
        <line x1="170" y1="90" x2="192" y2="90" stroke="#75A4FE" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 6" />
        {/* Small detail circle */}
        <circle cx="100" cy="80" r="3" fill="#75A4FE" />
      </svg>
      {/* Text */}
      <div style={{ textAlign:"center" }}>
        <p style={{ fontFamily:"'Manrope', sans-serif", fontWeight:700, fontSize:20, lineHeight:"27px", color:"#4A4A4A", margin:0 }}>
          {title}
        </p>
        {subtitle && (
          <p style={{ fontFamily:"'Manrope', sans-serif", fontWeight:400, fontSize:14, lineHeight:"20px", color:"#8E8E8E", margin:"4px 0 0" }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

/* ─── KPI Card ─── */
function KPICard({ iconPath, label, value, trend, trendLabel, trendUp, footer, onClick }) {
  const isIncome = label.toLowerCase().includes("ingresos");
  const isBalance = label.toLowerCase().includes("balance");
  const isExpense = label.toLowerCase().includes("gastos");

  const isMobileViewport = typeof window !== "undefined" && window.innerWidth <= 900;
  const displayTrendLabel = (isMobileViewport && trendLabel)
    ? trendLabel.replace(" vs mes anterior", "")
    : trendLabel;

  // Determine if the trend is good/bad for color selection:
  // - Income or Balance: positive trend is good (green).
  // - Expense: negative trend is good (green) / positive trend is bad (red).
  let isGood = true;
  if (isIncome || isBalance) {
    isGood = (trend || 0) >= 0;
  } else if (isExpense) {
    isGood = (trend || 0) < 0;
  }

  const badgeBg = isGood ? "#F0FDFD" : "#FDF2F2";
  const badgeColor = isGood ? "#62BF92" : "#EC5F5F";
  const isUp = (trend || 0) >= 0;

  let cardClass = "aorra-kpi-card";
  if (isIncome) cardClass += " aorra-kpi-card-income";
  else if (isExpense) cardClass += " aorra-kpi-card-expense";
  else if (isBalance) cardClass += " aorra-kpi-card-balance";

  return (
    <button
      type="button"
      className={cardClass}
      onClick={onClick}
      aria-label={`Ver ${label.toLowerCase()}`}
      style={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "12px 18px",
        flex: "1 1 160px",          /* Stretches to fill space equally, min-width 160px */
        // Keep every summary card aligned when the expenses card includes
        // its credit-card payment note.
        height: 208,
        background: "#FFFFFF",
        border: "none",
        textAlign: "left",
        boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
        borderRadius: 20,
        cursor: "pointer",
      }}
    >
      <div className="aorra-kpi-inner-container" style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: 0,
        gap: 5,
        width: "100%",
        height: "100%",
      }}>
        {/* Icon circle (Ellipse 1884) */}
        <div className="aorra-kpi-icon-circle" style={{
          position: "relative",
          width: 65,
          height: 65,
          borderRadius: "50%",
          background: "#F0FDFD",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}>
          <img 
            src={iconPath} 
            alt={label} 
            style={{ 
              width: label.toLowerCase().includes("balance") ? 41 : 42, 
              height: label.toLowerCase().includes("balance") ? 41 : 40,
              objectFit: "contain" 
            }} 
          />
        </div>

        {/* Text Block (Frame 2147224829) */}
        <div className="aorra-kpi-text-block" style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: 0,
          gap: 4,
          width: "100%",
          marginTop: 6,
        }}>
          <div className="aorra-kpi-text-titles" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {/* label: font-weight 700, 15px */}
            <span className="aorra-kpi-label" style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: 14,
              lineHeight: "20px",
              color: "#47484C",
              whiteSpace: "nowrap",
            }}>
              {label}
            </span>

            {/* value: font-weight 800, 24px */}
            <span className="aorra-kpi-value" style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              fontSize: 22,
              lineHeight: "30px",
              color: "#255E99",
              whiteSpace: "nowrap",
            }}>
              {value}
            </span>
          </div>

          {/* Trend badge */}
          {trend !== undefined && (
            <div className="aorra-kpi-trend-badge" style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "4px 10px",
              gap: 4,
              background: badgeBg,
              borderRadius: 15,
              height: 25,
              width: "fit-content",
            }}>
              {isUp ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={badgeColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <line x1="12" y1="19" x2="12" y2="5"></line>
                  <polyline points="5 12 12 5 19 12"></polyline>
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={badgeColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <polyline points="19 12 12 19 5 12"></polyline>
                </svg>
              )}

              {/* Trend percentage label */}
              <span className="aorra-kpi-trend-label" style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: 11,
                color: badgeColor,
                whiteSpace: "nowrap",
              }}>
                {displayTrendLabel}
              </span>
            </div>
          )}
          {footer && (
            <span style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: 10,
              lineHeight: "14px",
              color: "#5B6B85",
              whiteSpace: "normal",
            }}>
              {footer}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/* ─── Main Component ─── */
const TimePickerForm = ({ initialHour, onSave }) => {
  const [hour12, setHour12] = useState(() => {
    if (!initialHour) return "08";
    let h = parseInt(initialHour.split(":")[0]);
    if (h === 0) h = 12;
    if (h > 12) h -= 12;
    return h.toString().padStart(2, "0");
  });
  
  const [minute, setMinute] = useState(() => {
    if (!initialHour) return "00";
    return initialHour.split(":")[1] || "00";
  });
  
  const [ampm, setAmpm] = useState(() => {
    if (!initialHour) return "PM";
    const h = parseInt(initialHour.split(":")[0]);
    return h >= 12 ? "PM" : "AM";
  });

  const handleSave = () => {
    let h24 = parseInt(hour12);
    if (ampm === "PM" && h24 < 12) h24 += 12;
    if (ampm === "AM" && h24 === 12) h24 = 0;
    
    const finalHour = h24.toString().padStart(2, "0") + ":" + minute;
    onSave(finalHour);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <select value={hour12} onChange={e => setHour12(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd", flex: 1, fontFamily: "'Manrope', sans-serif", outline: "none" }}>
          {Array.from({length: 12}, (_, i) => {
            const h = (i + 1).toString().padStart(2, "0");
            return <option key={h} value={h}>{h}</option>;
          })}
        </select>
        <span style={{ fontWeight: 700, color: "#888" }}>:</span>
        <select value={minute} onChange={e => setMinute(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd", flex: 1, fontFamily: "'Manrope', sans-serif", outline: "none" }}>
          {Array.from({length: 60}, (_, i) => {
            const m = i.toString().padStart(2, "0");
            return <option key={m} value={m}>{m}</option>;
          })}
        </select>
        <select value={ampm} onChange={e => setAmpm(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd", flex: 1, fontFamily: "'Manrope', sans-serif", outline: "none" }}>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
      <Button onClick={handleSave} style={{ width: "100%", background: "#396BE9", color: "white", borderRadius: 8, fontWeight: 700 }}>Guardar</Button>
    </div>
  );
};

const getLocalHealthInsight = (summary) => {
  const score = Number(summary?.health_score || 0);
  const savingsRate = Number(summary?.savings_rate || 0);
  const debtRatio = Number(summary?.debt_ratio || 0);
  if (score >= 80) return "Vas por muy buen camino: conserva tu hábito de ahorro y revisa tus metas para seguir avanzando.";
  if (savingsRate < 0) return "Tus gastos superan tus ingresos en este periodo. Revisa las categorías principales antes de sumar nuevos gastos.";
  if (debtRatio > 35) return "Tu nivel de deuda merece atención. Prioriza las cuotas próximas y evita aumentar el saldo de tus tarjetas.";
  return "Tu panorama está estable. Revisa tus categorías principales para encontrar una oportunidad concreta de ahorro.";
};

const isCanceledRequest = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const outletCtx = useOutletContext() || {};
  const isExpired = outletCtx.isExpired || false;

  const currency = user?.currency || "COP";

  /* State */
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false); // separate from main loading — keeps chart visible
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [categorySummary, setCategorySummary] = useState(null);
  const [notificationPrefs, setNotificationPrefs] = useState({ payment_reminders_enabled:true, payment_reminder_hour:"20:00" });
  const [aiInsight, setAiInsight] = useState("Analiza tus últimos movimientos para optimizar tu flujo mensual.");
  const [loadingInsight, setLoadingInsight] = useState(false);

  // Every filter change creates a fresh request.  Keep only the latest result
  // so a slower, previous range can never overwrite the dashboard.
  const dashboardRequestRef = useRef(0);
  const carouselRef = useRef(null);
  const categoryChipsRef = useRef(null);
  const catSwipeRef = useRef({ touchStart: null, touchEnd: null });

  /* Date filter */
  const now = new Date();
  const [dateFilterType, setDateFilterType] = useState("this_month");
  const [dateRange, setDateRange] = useState({ from:null, to:null });
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  /* Chart toggle */
  const [chartView, setChartView] = useState("gastos");

  /* Category states */
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name:"", budget:"", color:"#396BE9", icon:"/Iconos/Categorias/Ahorros.png" });
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryOriginalName, setEditingCategoryOriginalName] = useState(null);
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [categoryDeleteFlow, setCategoryDeleteFlow] = useState(null);
  const [categoryDeleting, setCategoryDeleting] = useState(false);
  const [hoveredCat, setHoveredCat] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedCategoryPanel, setSelectedCategoryPanel] = useState(null);
  const [categoryPage, setCategoryPage] = useState(0);
  const [categoryTxs, setCategoryTxs] = useState([]);
  const [loadingCategoryTxs, setLoadingCategoryTxs] = useState(false);

  /* Notification time */
  const [reminderTimeOpen, setReminderTimeOpen] = useState(false);
  const [updatingNotifPrefs, setUpdatingNotifPrefs] = useState(false);

  /* Viewport responsive check */
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ─── Date helpers ─── */
  const getDateParams = () => {
    const today = new Date();
    let startDate, endDate;
    switch (dateFilterType) {
      case "esta_semana":
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
        endDate   = today;
        break;
      case "semana_pasada":
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 13);
        endDate   = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        break;
      case "this_month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate   = new Date(today.getFullYear(), today.getMonth()+1, 0);
        break;
      case "last_month":
        startDate = new Date(today.getFullYear(), today.getMonth()-1, 1);
        endDate   = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "custom":
        startDate = dateRange.from || new Date(today.getFullYear(), today.getMonth(), 1);
        endDate   = dateRange.to   || new Date(today.getFullYear(), today.getMonth()+1, 0);
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate   = new Date(today.getFullYear(), today.getMonth()+1, 0);
    }
    return { start_date: startDate.toISOString(), end_date: endDate.toISOString() };
  };

  const getDateLabel = () => {
    if (dateFilterType === "esta_semana") return "Esta Semana";
    if (dateFilterType === "semana_pasada") return "Semana Pasada";
    if (dateFilterType === "this_month") return "Este mes";
    if (dateFilterType === "last_month") return "Mes pasado";
    if (dateFilterType === "custom" && dateRange.from && dateRange.to) {
      return `${dateRange.from.toLocaleDateString("es-CO",{day:"numeric",month:"short"})} - ${dateRange.to.toLocaleDateString("es-CO",{day:"numeric",month:"short"})}`;
    }
    return "Personalizado";
  };

  const getChartData = () => {
    const today = new Date();
    let start = new Date();
    let end = new Date();
    let isMonthView = false;
    
    if (dateFilterType === "esta_semana") {
      start.setDate(today.getDate() - 6);
      end = today;
    } else if (dateFilterType === "semana_pasada") {
      start.setDate(today.getDate() - 13);
      end.setDate(today.getDate() - 7);
    } else if (dateFilterType === "this_month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      isMonthView = true;
    } else if (dateFilterType === "last_month") {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      isMonthView = true;
    } else {
      start = dateRange.from || new Date(today.getFullYear(), today.getMonth(), 1);
      end = dateRange.to || new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const diff = Math.ceil((end - start) / 86400000);
      if (diff > 10) isMonthView = true;
    }

    const dataPoints = [];
    const curr = new Date(start);
    curr.setHours(0,0,0,0);
    const limit = new Date(end);
    limit.setHours(23,59,59,999);

    const daysNames = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

    // Helper: convert a Date to YYYY-MM-DD string in local timezone
    const toLocalDateStr = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    // Pre-build a map of "YYYY-MM-DD" → transactions for O(1) lookups
    const txsByDate = {};
    (transactions || []).forEach(t => {
      if (!t.created_at) return;
      // Extract date portion from ISO string (e.g. "2026-07-01T17:30:00.000Z" → "2026-07-01")
      // This is timezone-safe: we compare the stored UTC date directly
      // Since Colombia is UTC-5, a transaction stored as "2026-07-01T02:00:00Z" would be
      // June 30 locally, but we treat the UTC date as-is here to match what the backend stores.
      // Alternatively parse properly in Bogota timezone:
      const rawDate = t.created_at.substring(0, 10); // "2026-07-01"
      if (!txsByDate[rawDate]) txsByDate[rawDate] = [];
      txsByDate[rawDate].push(t);
    });

    while (curr <= limit) {
      const dayKey = toLocalDateStr(curr); // "2026-07-01"
      const dayTxs = txsByDate[dayKey] || [];

      const dayExpenses = dayTxs
        .filter(t => t.type === "expense")
        .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

      const dayIncome = dayTxs
        .filter(t => t.type === "income")
        .reduce((sum, t) => sum + (t.amount || 0), 0);

      let label = "";
      if (isMonthView) {
        label = curr.getDate().toString();
      } else {
        label = daysNames[curr.getDay()];
      }

      dataPoints.push({
        name: label,
        value: chartView === "ingresos" ? dayIncome : dayExpenses,
        rawDate: new Date(curr)
      });

      curr.setDate(curr.getDate() + 1);
    }

    return { dataPoints, isMonthView };
  };

  /* ─── Fetch ─── */
  useEffect(() => { fetchAll();
  // fetchAll reads exactly these date controls; depending on its render-local
  // identity would create a request loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilterType, dateRange]);

  const fetchAll = async (retryCount = 0) => {
    const requestId = dashboardRequestRef.current + 1;
    dashboardRequestRef.current = requestId;
    if (!loading) setChartLoading(true);
    try {
      const params = getDateParams();
      const bootstrap = await api.get(
        `/dashboard/bootstrap?start_date=${params.start_date}&end_date=${params.end_date}`
      );
      if (requestId !== dashboardRequestRef.current) return;
      const data = bootstrap.data || {};
      const summaryData = data.summary || null;
      setSummary(summaryData);
      setTransactions(Array.isArray(data.transactions) ? data.transactions : []);
      setCreditCards(data.credit_cards || []);
      setUpcomingPayments(data.upcoming_payments || []);
      setCategorySummary(data.category_summary || null);
      setNotificationPrefs(data.notification_preferences || { payment_reminders_enabled:true, payment_reminder_hour:"20:00" });
      if (outletCtx.setGlobalHealthScore && summaryData?.health_score !== undefined) {
        outletCtx.setGlobalHealthScore(summaryData.health_score);
      }

      // Show the dashboard immediately
      setLoading(false);

      // Keep the dashboard informative without issuing one external AI request
      // per page load. Personalized AI can be requested explicitly elsewhere.
      if (summaryData) {
        setAiInsight(getLocalHealthInsight(summaryData));
        setLoadingInsight(false);
      }
    } catch (e) {
      if (requestId === dashboardRequestRef.current) {
        // A workspace can finish initializing while this request is in flight.
        // Retry once with the settled scope instead of reporting a false failure.
        if (isCanceledRequest(e)) {
          if (retryCount === 0) return fetchAll(1);
          return;
        }
        toast.error("Error al cargar datos");
        setLoading(false);
      }
    } finally {
      if (requestId === dashboardRequestRef.current) {
        setChartLoading(false);
      }
    }
  };

  /* ─── Categories ─── */
  const getCategories = () => {
    const breakdown = summary?.category_breakdown || [];
    const budgetMap = {};
    (categorySummary?.categories || []).forEach(c => { budgetMap[c.name] = c; });

    // Include budgeted/custom categories even when they have no movements yet.
    const categoryRows = [];
    const seenNames = new Set();
    for (const c of breakdown) {
      const amount = Math.abs(finiteNumber(c.value));
      if (c.name && !seenNames.has(c.name) && amount > 0) {
        seenNames.add(c.name);
        categoryRows.push({ name: c.name, amount });
      }
    }
    for (const c of (categorySummary?.categories || [])) {
      if (c.name && !seenNames.has(c.name)) {
        seenNames.add(c.name);
        categoryRows.push({ name: c.name, amount: Math.abs(finiteNumber(c.amount)) });
      }
    }

    return categoryRows
      .map((c, i) => {
        const extra = budgetMap[c.name] || {};
        return {
          name: c.name, amount: finiteNumber(c.amount),
          id: extra.id || null,
          budget: extra.budget || 0,
          color: extra.color || CHART_COLORS[i % CHART_COLORS.length],
          icon: extra.icon || "more-horizontal",
        };
      })
      .sort((a,b) => b.amount - a.amount);
  };

  const openCategoryDialog = (cat = null) => {
    setCategoryDeleteFlow(null);
    setCategoryDeleting(false);
    if (cat) {
      setEditingCategoryId(cat.id || null);
      setEditingCategoryOriginalName(cat.name || null);
      const defaultEmoji = DEFAULT_CAT_EMOJI[cat.name] || "🍔";
      const iconVal = (cat.icon && cat.icon !== "more-horizontal" && cat.icon !== "shopping-bag") ? cat.icon : defaultEmoji;
      setCategoryForm({ name: cat.name||"", budget: cat.budget ? cat.budget.toString():"", color: cat.color||"#396BE9", icon: iconVal });
    } else {
      setEditingCategoryId(null);
      setEditingCategoryOriginalName(null);
      setCategoryForm({ name:"", budget:"", color:"#396BE9", icon:"🍔" });
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name) { toast.error("El nombre es requerido"); return; }
    setCategorySubmitting(true);
    try {
      const payload = { name: categoryForm.name, budget: categoryForm.budget ? parseFloat(categoryForm.budget):0, color: categoryForm.color, icon: categoryForm.icon };
      if (editingCategoryId) await api.put(`/category-budgets/${editingCategoryId}`, payload);
      else await api.post("/category-budgets", payload);
      toast.success(editingCategoryId ? "Categoría actualizada" : "Categoría creada");
      setCategoryDialogOpen(false);
      fetchAll();
    } catch { toast.error("Error al guardar categoría"); }
    finally { setCategorySubmitting(false); }
  };

  const closeCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setCategoryDeleteFlow(null);
    setCategoryDeleting(false);
  };

  const handleDeleteCategory = async () => {
    const identity = { name: editingCategoryOriginalName || categoryForm.name, budget_id: editingCategoryId || null };
    setCategoryDeleting(true);
    try {
      const { data: preview } = await api.post("/categories/delete-preview", identity);
      if (!preview.requires_resolution) {
        await api.post("/categories/delete", identity);
        toast.success("Categoría eliminada");
        closeCategoryDialog();
        fetchAll();
        return;
      }
      setCategoryDeleteFlow({ step: "choice", preview, destination: "", error: "" });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al revisar la categoría");
    } finally {
      setCategoryDeleting(false);
    }
  };

  const confirmCategoryDeletion = async (strategy) => {
    if (!categoryDeleteFlow) return;
    if (strategy === "move" && !categoryDeleteFlow.destination) {
      setCategoryDeleteFlow({ ...categoryDeleteFlow, error: "Selecciona la categoría de destino." });
      return;
    }
    setCategoryDeleting(true);
    try {
      const payload = {
        name: categoryDeleteFlow.preview.category,
        budget_id: categoryDeleteFlow.preview.budget_id || null,
        strategy,
        destination: strategy === "move" ? categoryDeleteFlow.destination : null,
      };
      const { data } = await api.post("/categories/delete", payload);
      toast.success(strategy === "move"
        ? `${data.moved_transactions} movimientos movidos a ${data.destination}`
        : `Categoría y ${data.deleted_transactions} movimientos eliminados`);
      closeCategoryDialog();
      fetchAll();
    } catch (error) {
      const message = error?.response?.data?.detail || "No se pudo eliminar la categoría";
      setCategoryDeleteFlow((current) => current ? { ...current, error: message } : current);
      toast.error(message);
    } finally {
      setCategoryDeleting(false);
    }
  };

  const openCategoryPanel = async (cat) => {
    setSelectedCategoryPanel(cat);
    setLoadingCategoryTxs(true);
    setCategoryTxs([]);
    try {
      const params = getDateParams();
      const res = await api.get(`/transactions?category=${encodeURIComponent(cat.name)}&start_date=${params.start_date}&end_date=${params.end_date}&limit=4`);
      setCategoryTxs((res.data || []).slice(0,4));
    } catch { setCategoryTxs([]); }
    finally { setLoadingCategoryTxs(false); }
  };

  const scrollPaymentsRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const scrollPaymentsLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  /* ─── Notification ─── */
  const toggleNotifPrefs = async () => {
    setUpdatingNotifPrefs(true);
    try {
      const newVal = !notificationPrefs.payment_reminders_enabled;
      await api.put("/user/notification-preferences", { payment_reminders_enabled: newVal, payment_reminder_hour: notificationPrefs.payment_reminder_hour||"20:00" });
      setNotificationPrefs({ ...notificationPrefs, payment_reminders_enabled: newVal });
      toast.success(newVal ? "Notificaciones activadas" : "Notificaciones desactivadas");
    } catch { toast.error("Error al actualizar preferencias"); }
    finally { setUpdatingNotifPrefs(false); }
  };

  const updateReminderHour = async (hour) => {
    try {
      await api.put("/user/notification-preferences", { payment_reminders_enabled: notificationPrefs.payment_reminders_enabled, payment_reminder_hour: hour });
      setNotificationPrefs({ ...notificationPrefs, payment_reminder_hour: hour });
      toast.success("Hora actualizada");
      setReminderTimeOpen(false);
    } catch { toast.error("Error al actualizar hora"); }
  };

  const formatReminderTime = (h24) => {
    const [h, m] = (h24||"20:00").split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${dh}:${m.toString().padStart(2,"0")} ${ampm}`;
  };

  /* ─── Loading skeleton ─── */
  if (loading) {
    return (
      <div style={{ display:"flex", gap:20, padding:"20px 0", flexWrap:"wrap" }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ flex:1, minWidth:180, height:160, borderRadius:20, background:"#e9ecef", animation:"pulse 1.5s infinite" }} />
        ))}
      </div>
    );
  }

  const cats = getCategories();
  const activeCat = activeCategory || cats[0] || { name: "Sin datos", amount: 0, color: "#e0e0e0", icon: "📊" };
  const displayCat = hoveredCat || activeCat;
  const totalExpenses = Math.abs(finiteNumber(summary?.month_expenses));
  const isCreditCardExpense = (tx) => tx?.type === "expense" && tx?.payment_method && tx.payment_method !== "cash";
  const getCreditCardName = (tx) => creditCards.find((card) => card.id === tx?.payment_method)?.name || "Tarjeta de crédito";
  const scoreLabel = (summary?.health_score||0) >= 80 ? "Excelente" : (summary?.health_score||0) >= 50 ? "Regular" : "Crítica";

  /* ─── Chart data: use backend weekly_activity (already filtered by date range) ─── */
  const rawActivity = summary?.weekly_activity || [];
  const isMonthView = rawActivity.length > 10;
  const chartData = rawActivity.map(w => ({
    name: w.name,
    value: chartView === "ingresos" ? (w.ingresos || 0) : (w.gastos || 0),
    rawDate: w.date ? new Date(w.date) : null,
  }));

  
  // --- JSX FRAGMENTS FOR MOBILE/DESKTOP RESPONSIVE LAYOUT ---
  const headerRowJSX = (
    <div className="aorra-dashboard-heading">
            <h1 className="aorra-dashboard-title" style={{ fontFamily:"'Manrope', sans-serif", fontWeight:700, fontSize:22, color:"#255E99", margin:0 }}>
              Mi Dashboard
            </h1>

            {/* New Figma Date Filter Chip Trigger */}
            <div className="aorra-date-filter">
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "0 16px 0 8px",
                    gap: 8,
                    width: "fit-content",
                    minWidth: 182,
                    height: 49,
                    background: "#E1E9FC",
                    borderRadius: 32,
                    border: "none",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    transition: "opacity 0.15s ease",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = 0.9}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = 1}
                >
                  {/* Calendar Icon BG Circle */}
                  <div style={{
                    width: 34,
                    height: 34,
                    borderRadius: 100,
                    background: "#4D72F5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
                  }}>
                    {/* White Calendar SVG */}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM19 7H5V6H19V7Z" fill="#FFFFFF"/>
                    </svg>
                  </div>

                  {/* Label container */}
                  <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: 1,
                    gap: 8,
                  }}>
                    <span style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: "25px",
                      color: "#396BE9",
                      whiteSpace: "nowrap",
                    }}>
                      {getDateLabel()}
                    </span>

                    {/* Arrow chevron */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24 }}>
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L5 5L9 1" stroke="#396BE9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-3xl shadow-2xl border-0 overflow-hidden" align="start"
                style={{ background:"rgba(255,255,255,0.95)", backdropFilter:"blur(24px)" }}>
                <div style={{ padding: 12 }}>
                  {/* Calendar quick filter buttons inside popover */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 12, borderBottom: "1px solid #f0f0f0", paddingBottom: 8 }}>
                    <button
                      onClick={() => { setDateFilterType("this_month"); setDatePickerOpen(false); }}
                      style={{
                        padding: "6px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontSize: 12, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
                        background: dateFilterType === "this_month" ? "#396BE9" : "#f5f5f5",
                        color: dateFilterType === "this_month" ? "#fff" : "#396BE9"
                      }}
                    >Este mes</button>
                    <button
                      onClick={() => { setDateFilterType("last_month"); setDatePickerOpen(false); }}
                      style={{
                        padding: "6px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontSize: 12, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
                        background: dateFilterType === "last_month" ? "#396BE9" : "#f5f5f5",
                        color: dateFilterType === "last_month" ? "#fff" : "#396BE9"
                      }}
                    >Mes pasado</button>
                  </div>

                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(r) => {
                      setDateRange(r || { from:null, to:null });
                      if (r?.from && r?.to) { setDateFilterType("custom"); setDatePickerOpen(false); }
                    }}
                    numberOfMonths={1}
                  />
                </div>
              </PopoverContent>
            </Popover>
            </div>
            <button type="button" className="aorra-cash-summary" aria-label="Ver cuentas y efectivo" onClick={() => navigate("/dashboard/credit-cards")}>
              <span>Dinero disponible</span>
              <strong>{fmt(summary?.cash_available || 0, currency)}</strong>
            </button>
          </div>
  );

  const kpiCardsJSX = (
    <div className="aorra-kpi-card-container">
            <KPICard
              iconPath="/Iconos/icono ingresos dashboard.png"
              label="Ingresos"
              value={fmtKPI(summary?.total_income||0, currency)}
              trend={(summary?.income_trend||0)}
              trendLabel={`${(summary?.income_trend||0) >= 0 ? "+" : ""}${(summary?.income_trend||0).toFixed(0)}% vs mes anterior`}
              trendUp={(summary?.income_trend||0) >= 0}
              onClick={() => navigate("/dashboard/transactions?type=income")}
            />
            <KPICard
              iconPath="/Iconos/Dashboard/gastos.png"
              label="Gastos"
              value={fmtKPI(summary?.month_expenses||0, currency)}
              trend={(summary?.expense_trend||0)}
              trendLabel={`${(summary?.expense_trend||0) >= 0 ? "+" : ""}${(summary?.expense_trend||0).toFixed(0)}% vs mes anterior`}
              trendUp={(summary?.expense_trend||0) <= 0}
              footer={(summary?.month_credit_card_expenses || 0) > 0
                ? `💳 ${fmtKPI(summary.month_credit_card_expenses, currency)} con tarjeta · no afecta tu balance`
                : null}
              onClick={() => navigate("/dashboard/transactions?type=expense")}
            />
            <KPICard
              iconPath="/Iconos/Dashboard/balance.png"
              label="Balance"
              value={fmtKPI(summary?.balance||0, currency)}
              trend={(summary?.balance||0)}
              trendLabel={`${(summary?.total_income ? ((summary?.balance||0)/summary.total_income)*100 : 0) >= 0 ? "+" : ""}${(summary?.total_income ? ((summary?.balance||0)/summary.total_income)*100 : 0).toFixed(0)}% vs mes anterior`}
              trendUp={(summary?.balance||0) >= 0}
              onClick={() => navigate("/dashboard/credit-cards")}
            />
    </div>
  );

  const chartCardJSX = (
    <div style={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "20px 24px",
            gap: 16,
            width: "100%",
            height: 405,
            background: "#FFFFFF",
            border: "1px solid #F1F3F7",
            boxShadow: "0px 13px 25px 2px rgba(132, 140, 166, 0.13)",
            borderRadius: 32,
          }}>
            {/* Chart header row */}
            <div className="aorra-chart-header" style={{
              width: "100%",
              height: "auto",
              minHeight: 48,
            }}>
              <h3 style={{ fontFamily:"'Manrope', sans-serif", fontWeight:700, fontSize:"22px", color:"#255E99", margin:0 }}>
                Tus estadísticas
              </h3>

              {/* Component 1 (Ingresos / Gastos Toggle) */}
              <div className="aorra-chart-toggle">
                <button
                  onClick={() => setChartView("ingresos")}
                  className="aorra-chart-toggle-btn"
                  style={{
                    background: chartView === "ingresos" ? "#396BE9" : "transparent",
                    color: chartView === "ingresos" ? "#FFFFFF" : "#1E2029",
                    opacity: chartView === "ingresos" ? 1 : 0.6,
                    letterSpacing: "-0.018em",
                  }}
                >
                  Ingresos
                </button>
                <button
                  onClick={() => setChartView("gastos")}
                  className="aorra-chart-toggle-btn"
                  style={{
                    background: chartView === "gastos" ? "#396BE9" : "transparent",
                    color: chartView === "gastos" ? "#FFFFFF" : "#1E2029",
                    opacity: chartView === "gastos" ? 1 : 0.6,
                    letterSpacing: "-0.018em",
                  }}
                >
                  Gastos
                </button>
              </div>
            </div>

            {/* Chart tabs */}
            <div className="aorra-chart-tabs" style={{ display:"flex", gap:0, borderBottom:"1px solid #f0f0f0", width: "100%", height: 40, position: "relative", overflowX: "auto", scrollbarWidth: "none", whiteSpace: "nowrap", marginTop: isMobile ? 8 : 0 }}>
              {[
                { key:"esta_semana", label:"Esta Semana" },
                { key:"semana_pasada", label:"Semana Pasada" },
                { key:"this_month", label:"Este mes" },
                { key:"last_month", label:"Mes pasado" },
              ].map(t => (
                <button key={t.key}
                  onClick={() => setDateFilterType(t.key)}
                  style={{
                    padding:"8px 16px", border:"none", background:"transparent", cursor:"pointer",
                    fontFamily:"'Manrope', sans-serif", fontWeight:600, fontSize:14, letterSpacing:"-0.018em",
                    color: "#1E2029",
                    opacity: dateFilterType === t.key ? 1 : 0.6,
                    borderBottom: dateFilterType === t.key ? "2px solid #396BE9" : "2px solid transparent",
                    marginBottom:-1,
                    transition: "all 0.2s ease",
                    flexShrink: 0,
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Line chart */}
            <div style={{ width: "100%", height: 247, display: "flex", flexDirection: "column", justifyContent: "flex-end", position: "relative" }}>
              {/* Subtle loading indicator — chart stays visible, only opacity dims slightly */}
              {chartLoading && (
                <div style={{
                  position: "absolute", top: 0, right: 0,
                  display: "flex", alignItems: "center", gap: 6,
                  background: "rgba(255,255,255,0.85)", borderRadius: 20,
                  padding: "4px 10px", zIndex: 10,
                  fontSize: 11, fontWeight: 700, color: "#396BE9",
                  fontFamily: "'Manrope', sans-serif",
                  boxShadow: "0 2px 8px rgba(57,107,233,0.15)"
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#396BE9",
                    animation: "chartPulse 0.8s ease-in-out infinite alternate"
                  }} />
                  Actualizando...
                </div>
              )}
              <div style={{ opacity: chartLoading ? 0.7 : 1, transition: "opacity 0.2s ease" }}>
              <ResponsiveContainer width="100%" height={210}>
                <AreaChart data={chartData} margin={{ top:10, right:20, left:0, bottom:0 }}>
                  {/* Gradient definition for the area fill */}
                  <defs>
                    <linearGradient id="chartAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#396BE9" stopOpacity={0.18} />
                      <stop offset="70%"  stopColor="#396BE9" stopOpacity={0.06} />
                      <stop offset="100%" stopColor="#396BE9" stopOpacity={0.0}  />
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="name" 
                    stroke="#1E2029" 
                    strokeOpacity={0.4} 
                    fontSize={12} 
                    fontWeight={700} 
                    axisLine={false} 
                    tickLine={false} 
                    fontFamily="'Manrope', sans-serif" 
                    letterSpacing="-0.018em"
                    ticks={isMonthView ? ["1", "5", "10", "15", "20", "25", "30"] : undefined}
                    tick={props => {
                      const isLast = props.index === chartData.length - 1;
                      const isToday = !isMonthView && isLast && dateFilterType === "esta_semana";
                      return (
                        <text
                          x={props.x}
                          y={props.y + 16}
                          fill="#1E2029"
                          textAnchor="middle"
                          fontFamily="'Manrope', sans-serif"
                          fontSize="12"
                          fontWeight="700"
                          opacity={isToday ? 1 : 0.4}
                        >
                          {props.payload.value}{isToday ? " (hoy)" : ""}
                        </text>
                      );
                    }}
                  />
                  <YAxis 
                    stroke="#1E2029" 
                    strokeOpacity={0.4} 
                    fontSize={isMobile ? 10 : 12} 
                    fontWeight={700} 
                    axisLine={false} 
                    tickLine={false} 
                    fontFamily="'Manrope', sans-serif" 
                    width={isMobile ? 50 : 65}
                    tickFormatter={v => {
                      if (v === 0) return '$0';
                      if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
                      if (Math.abs(v) >= 1000) return `$${Math.round(v / 1000)}K`;
                      return `$${v}`;
                    }}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius:16, border:"none", boxShadow:"0 8px 24px rgba(0,0,0,0.12)", fontFamily:"'Manrope', sans-serif", fontWeight:700 }}
                    formatter={v => [fmt(v, currency), chartView==="ingresos"?"Ingresos":"Gastos"]}
                  />
                  <Area
                    type="natural"
                    dataKey="value"
                    stroke="#396BE9"
                    strokeWidth={3}
                    fill="url(#chartAreaGradient)"
                    dot={false}
                    activeDot={{ r:6, fill:"#396BE9", strokeWidth:0 }}
                    style={{ filter:"drop-shadow(0px 4px 16px rgba(57,107,233,0.3))" }}
                    isAnimationActive={true}
                    animationDuration={350}
                    animationEasing="ease-out"
                  />
                </AreaChart>
              </ResponsiveContainer>
              </div> {/* end opacity wrapper */}
            </div>
          </div>
  );

  const upcomingPaymentsJSX = (
    <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "0px",
            gap: "24px",
            width: "100%",
            height: "auto",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", flexWrap: isMobile ? "wrap" : "nowrap", gap: 12 }}>
              <h3 style={{
                margin: 0,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: "22px",
                lineHeight: "33px",
                color: "#255E99",
              }}>
                Tus próximos pagos 🤑
              </h3>

              {/* Right side controls */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, width: isMobile ? "100%" : "auto" }}>
                {/* Backward carousel arrow, intentionally before the WhatsApp control */}
                {!isMobile && (
                  <button
                    onClick={scrollPaymentsLeft}
                    aria-label="Ver pagos anteriores"
                    title="Ver pagos anteriores"
                    style={{
                      width: "39.7px",
                      height: "39.7px",
                      borderRadius: "12px",
                      background: "#E1E9FC",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#396BE9",
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                    }}
                    className="aorra-carousel-nav"
                  >
                    <ChevronLeft size={18} />
                  </button>
                )}
                {/* WhatsApp toggle */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 12,
                  background: "#D8FFEC",
                  borderRadius: 12,
                  padding: "8px 14px",
                  height: "39.7px",
                  width: isMobile ? "100%" : "auto",
                  boxSizing: "border-box",
                }}>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: "15px", color: "#255E99" }}>Avísame por whatsapp</span>
                  <Switch
                    className="aorra-whatsapp-switch"
                    checked={notificationPrefs.payment_reminders_enabled}
                    onCheckedChange={toggleNotifPrefs}
                    disabled={updatingNotifPrefs}
                  />
                  {notificationPrefs.payment_reminders_enabled && (
                    <Popover open={reminderTimeOpen} onOpenChange={setReminderTimeOpen}>
                      <PopoverTrigger asChild>
                        <button style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 8, border: "none", background: "rgba(57,107,233,0.1)", color: "#396BE9", cursor: "pointer", fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 11 }}>
                          <Clock size={12} />
                          {formatReminderTime(notificationPrefs.payment_reminder_hour)}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 rounded-2xl shadow-2xl border-0" align={isMobile ? "center" : "end"}
                        style={{
                          background: "rgba(255,255,255,0.95)",
                          backdropFilter: "blur(24px)",
                          width: isMobile ? "calc(100vw - 32px)" : "224px",
                          boxSizing: "border-box"
                        }}>
                        <div style={{ padding: "16px 16px 8px" }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Hora del recordatorio</p>
                          <TimePickerForm 
                            initialHour={notificationPrefs.payment_reminder_hour} 
                            onSave={updateReminderHour} 
                          />
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>

                {/* Carousel arrow */}
                {!isMobile && (
                  <button
                    onClick={scrollPaymentsRight}
                    style={{
                      width: "39.7px",
                      height: "39.7px",
                      borderRadius: "12px",
                      background: "#E1E9FC",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#396BE9",
                      transition: "all 0.2s ease",
                    }}
                    className="aorra-carousel-nav"
                  >
                    <ArrowRight size={18} />
                  </button>
                )}
              </div>
            </div>

            {/* Payment cards row (Horizontal Carousel) */}
            <div
              ref={carouselRef}
              className="aorra-payment-carousel"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: "0px",
                gap: "16px",
                width: "100%",
                height: "auto",
                overflowX: "auto",
                scrollBehavior: "smooth",
                scrollbarWidth: "none",
              }}
            >
              {upcomingPayments.length === 0 && (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "124px",
                  background: "#FFFFFF",
                  boxShadow: "-2px 0px 30.4px rgba(176, 179, 189, 0.1)",
                  borderRadius: "17px",
                  gap: "12px",
                }}>
                  <p style={{ color: "#aaa", fontSize: 14, fontFamily: "'Manrope', sans-serif", margin: 0 }}>
                    No hay próximos pagos
                  </p>
                  <button
                    onClick={() => navigate("/dashboard/subscriptions")}
                    style={{
                      background: "#E1E9FC",
                      color: "#396BE9",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "12px",
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 700,
                      fontSize: "13px",
                      cursor: "pointer",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = 0.8}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = 1}
                  >
                    + Agregar suscripción
                  </button>
                </div>
              )}
              {upcomingPayments.slice(0, 5).map((p, i) => {
                const dueDate = new Date(p.due_date);
                const isCard = p.type === "credit_card" || p.name.toLowerCase().includes("tc") || p.name.toLowerCase().includes("tarjeta");
                const isVisa = p.name.toLowerCase().includes("visa");
                const cardBg = isVisa ? "#353535" : "#ADE5F9";
                return (
                  <div
                    key={i}
                    className="aorra-payment-card"
                    style={{
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      padding: "16px 14px",
                      gap: "10px",
                      height: "124px",
                      background: "#FFFFFF",
                      boxShadow: "-2px 0px 30.4px rgba(176, 179, 189, 0.1)",
                      borderRadius: "17px",
                    }}
                  >
                    {/* cuenta+simbolo */}
                    <div style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      padding: "0px",
                      gap: "14px",
                      width: "100%",
                      height: "84px",
                    }}>
                      {/* Logo container */}
                      <div style={{
                        width: isCard ? "72px" : "56px",
                        height: isCard ? "50px" : "56px",
                        borderRadius: isCard ? "8px" : "50%",
                        background: isCard ? cardBg : "#CDE3F3",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {isCard ? (
                          p.icon && typeof p.icon === "string" && p.icon.startsWith("/") ? (
                            <img src={p.icon} alt={p.name} style={{ width: 40, height: 28, objectFit: "contain" }} />
                          ) : isVisa ? (
                            <span style={{
                              color: "#FFFFFF",
                              fontFamily: "'Manrope', sans-serif",
                              fontStyle: "italic",
                              fontWeight: "900",
                              fontSize: "16px",
                              letterSpacing: "-0.05em",
                            }}>VISA</span>
                          ) : (
                            <svg width="28" height="16" viewBox="0 0 30 18">
                              <circle cx="9" cy="9" r="9" fill="#EB001B" />
                              <circle cx="21" cy="9" r="9" fill="#F79E1B" fillOpacity="0.85" />
                            </svg>
                          )
                        ) : (
                          getServiceIcon(p.name, p.type, p.icon)
                        )}
                      </div>

                      {/* content-txt */}
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        padding: "0px",
                        gap: "12px",
                        flex: 1,
                        minWidth: 0,
                      }}>
                        {/* txt-saldo */}
                        <div style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}>
                          <span style={{
                            fontFamily: "'Manrope', sans-serif",
                            fontWeight: 700,
                            fontSize: "16px",
                            lineHeight: "22px",
                            color: "#4B4B4B",
                            textTransform: "capitalize",
                            minWidth: 0,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                            {p.name}
                          </span>
                          <span style={{
                            fontFamily: "'Manrope', sans-serif",
                            fontWeight: 800,
                            fontSize: "16px",
                            lineHeight: "22px",
                            color: "#4B4B4B",
                            flexShrink: 0,
                          }}>
                            {fmt(p.amount, currency)}
                          </span>
                        </div>

                        {/* Date badge */}
                        <div style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          padding: "8px 12px",
                          gap: "8px",
                          width: "100%",
                          height: "38px",
                          background: "#F5FAFF",
                          borderRadius: "16px",
                          boxSizing: "border-box",
                        }}>
                          <CalendarDays size={14} color="#1648CE" />
                          <span style={{
                            fontFamily: "'Manrope', sans-serif",
                            fontWeight: 400,
                            fontSize: "13px",
                            lineHeight: "18px",
                            color: "#2B50AF",
                          }}>
                            {formatUpcomingDate(dueDate)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
  );

  const categoriesJSX = (
    <div style={{ ...card, display: "flex", flexDirection: "column", gap: "16px", minHeight: "411px" }}>
            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width: "100%" }}>
              <h3 style={{ fontFamily:"'Manrope', sans-serif", fontWeight:700, fontSize:"22px", lineHeight:"33px", color:"#255E99", margin:0 }}>Categorías</h3>
              <button
                onClick={() => openCategoryDialog()}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 20px", borderRadius:32, border:"none", background:"#E1E9FC", cursor:"pointer", fontFamily:"'Manrope', sans-serif", fontWeight:700, fontSize:13, color:"#396BE9" }}
              >
                <Plus size={14} /> Nueva categoría
              </button>
            </div>

            {cats.length === 0 ? (
              <EmptyState title="Sin resultados" subtitle="No hay categorías creadas" />
            ) : (
            <>
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "24px", width: "100%", alignItems: "center", marginTop: "16px" }}>
              {/* LEFT SIDE: List Container */}
              <div style={{ display: "flex", flexDirection: "column", gap: "14px", width: isMobile ? "100%" : "60%", order: isMobile ? 2 : 1 }}>
                {(() => {
                  const visibleCats = cats.slice(categoryPage * 5, (categoryPage + 1) * 5);
                  const totalPages = Math.ceil(cats.length / 5);
                  
                  const handleTouchStart = (e) => {
                    catSwipeRef.current.touchEnd = null;
                    catSwipeRef.current.touchStart = e.targetTouches[0].clientX;
                  };
                  const handleTouchMove = (e) => {
                    catSwipeRef.current.touchEnd = e.targetTouches[0].clientX;
                  };
                  const handleTouchEnd = () => {
                    if (!catSwipeRef.current.touchStart || !catSwipeRef.current.touchEnd) return;
                    const distance = catSwipeRef.current.touchStart - catSwipeRef.current.touchEnd;
                    if (distance > 50 && categoryPage < totalPages - 1) {
                      setCategoryPage(categoryPage + 1);
                    }
                    if (distance < -50 && categoryPage > 0) {
                      setCategoryPage(categoryPage - 1);
                    }
                  };

                  return (
                    <div 
                      onTouchStart={handleTouchStart} 
                      onTouchMove={handleTouchMove} 
                      onTouchEnd={handleTouchEnd}
                      style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%", minHeight: "260px", overflowX: "hidden" }}
                    >
                      {visibleCats.map((cat, idx) => {
                        const pct = totalExpenses > 0
                          ? Math.min(100, Math.max(0, (finiteNumber(cat.amount) / totalExpenses) * 100))
                          : 0;
                        const isSelected = displayCat.name === cat.name;
                        return (
                          <div
                            key={`${cat.name}-${idx}`}
                            onClick={() => setSelectedCategory(cat)}
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              width: "100%",
                              height: "44px",
                              gap: "10px",
                              cursor: "pointer",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              background: isSelected ? "rgba(225, 233, 252, 0.6)" : "transparent",
                              boxSizing: "border-box",
                              transition: "background 0.2s"
                            }}
                            onMouseEnter={() => setHoveredCat(cat)}
                            onMouseLeave={() => setHoveredCat(null)}
                          >
                            {/* Icon */}
                            <div style={{
                              width: "34px",
                              height: "34px",
                              borderRadius: "50%",
                              background: "#F4F7FF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0
                            }}>
                              {getCatIcon(cat.icon, cat.name, 20)}
                            </div>
                            
                            <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: "4px", marginLeft: "4px", minWidth: 0 }}>
                               <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                 <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: cat.color || "#396BE9", flexShrink: 0 }} />
                                 <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: "14px", color: "#1B2559", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{cat.name}</span>
                               </div>
                               <div style={{ height: "6px", background: "#F4F7FF", borderRadius: "3px", overflow: "hidden", width: "100%" }}>
                                 <div style={{ height: "100%", width: `${pct}%`, background: cat.color || "#396BE9", borderRadius: "3px" }} />
                               </div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, marginLeft: "6px" }}>
                               <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "14px", color: isSelected ? (cat.color || "#396BE9") : "#6B7280" }}>{pct.toFixed(0)}%</span>
                               <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 500, fontSize: "12px", color: "#8F9BBA" }}>{fmtCompact(cat.amount, currency)}</span>
                            </div>

                            {/* Action Button: Edit Only */}
                            <div style={{ display: "flex", alignItems: "center", gap: "2px", marginLeft: "4px", flexShrink: 0 }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCategoryDialog(cat);
                                }}
                                title="Editar categoría"
                                style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center" }}
                              >
                                <Edit2 size={14} color="#6B7280" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "auto", paddingTop: "4px" }}>
                          {Array.from({ length: totalPages }).map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setCategoryPage(i)}
                              style={{ width: 8, height: 8, borderRadius: "50%", padding: 0, border: "none", background: categoryPage === i ? "#396BE9" : "#E1E9FC", cursor: "pointer", transition: "background 0.2s" }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* RIGHT SIDE: Chart */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: isMobile ? "100%" : "40%", position: "relative", order: isMobile ? 1 : 2 }}>
                <div style={{ position:"relative", width:220, height:220, flexShrink:0 }}>
                  <svg viewBox="0 0 100 100" style={{ width:"100%", height:"100%", transform:"rotate(-90deg)", overflow:"visible" }}>
                    <circle cx="50" cy="50" r="38" fill="none" stroke="#F4F7FF" strokeWidth="10" />
                    {(() => {
                      const N = cats.length;
                      const circ = 2 * Math.PI * 38;
                      const gapPerSegment = N > 1 ? 3 : 0;
                      const totalGapSpace = N * gapPerSegment;
                      const availableArc = circ - totalGapSpace;

                      const circleList = cats.map((cat, i) => {
                        const pct = totalExpenses > 0
                          ? Math.min(1, Math.max(0, finiteNumber(cat.amount) / totalExpenses))
                          : 0;
                        const segmentLength = Math.max(0.5, pct * availableArc);
                        const dashArr = `${segmentLength} ${circ}`;
                        const prevLength = cats.slice(0, i).reduce(
                          (acc, c) => acc + Math.min(1, Math.max(0, finiteNumber(c.amount) / Math.max(totalExpenses, 1))) * availableArc,
                          0,
                        );
                        const dashOffset = -(prevLength + i * gapPerSegment + gapPerSegment / 2);
                        const isSelected = displayCat.name === cat.name;
                        const sliceStroke = isSelected ? (cat.color || "#396BE9") : "#E1E9FC";

                        return {
                          cat,
                          i,
                          isSelected,
                          sliceStroke,
                          dashArr,
                          dashOffset
                        };
                      });

                      // Sort so non-selected grey circles render FIRST, selected colored circle renders LAST (on top of all others)!
                      circleList.sort((a, b) => (a.isSelected === b.isSelected ? 0 : a.isSelected ? 1 : -1));

                      return circleList.map((item) => (
                        <circle key={`${item.cat.name}-${item.i}`} cx="50" cy="50" r="38" fill="none"
                          stroke={item.sliceStroke}
                          strokeWidth={item.isSelected ? 14 : 10}
                          strokeDasharray={item.dashArr}
                          strokeDashoffset={item.dashOffset}
                          strokeLinecap="round"
                          style={{ transition:"all 0.25s", cursor:"pointer" }}
                          onMouseEnter={() => setHoveredCat(item.cat)}
                          onMouseLeave={() => setHoveredCat(null)}
                          onClick={() => setSelectedCategory(item.cat)}
                        />
                      ));
                    })()}
                  </svg>
                  {/* center text: CLICKING HERE NAVIGATES TO MOVEMENTS */}
                  <div 
                    onClick={() => navigate(`/dashboard/transactions?category=${encodeURIComponent(displayCat.name)}`)}
                    style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", cursor: "pointer", pointerEvents: "auto", borderRadius: "50%" }}
                    title={`Ver movimientos de ${displayCat.name}`}
                  >
                    {/* Category icon */}
                    <div style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: displayCat.color ? `${displayCat.color}22` : "#E1E9FC",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "4px",
                    }}>
                      {getCatIcon(displayCat.icon, displayCat.name, 26)}
                    </div>
                    {/* Amount */}
                    <span style={{ fontFamily:"'Manrope', sans-serif", fontWeight:800, fontSize:"20px", color:"#000000", lineHeight:"28px", letterSpacing: "-0.5px" }}>
                      {fmtCompact(displayCat.amount, currency)}
                    </span>
                    {/* Category Name Link */}
                    <span
                      style={{
                        fontFamily:"'Manrope', sans-serif",
                        fontWeight:700,
                        fontSize:"13px",
                        color: displayCat.color || "#396BE9",
                        textDecoration: "none",
                        marginTop: "2px"
                      }}
                    >
                      {displayCat.name} ➔
                    </span>
                  </div>
                </div>
              </div>
            </div>
            </>
            )}
          </div>
  );

  const fastAccessJSX = (
    <div className="aorra-quick-actions-card" style={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            padding: "27px 22px",
            width: "100%",
            maxWidth: "544px",
            height: "216px",
            background: "#FFFFFF",
            boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
            borderRadius: "20px",
          }}>
            <div className="aorra-quick-actions-content" style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "0px",
              gap: "20px",
              width: "100%",
              maxWidth: "461px",
              height: "173px",
            }}>
              <h3 style={{
                margin: 0,
                width: "100%",
                height: "33px",
                fontFamily: "'Manrope', sans-serif",
                fontStyle: "normal",
                fontWeight: 700,
                fontSize: "22px",
                lineHeight: "33px",
                color: "#255E99",
              }}>
                Acciones rápidas
              </h3>
              <div className="aorra-quick-actions-container" style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0px",
                gap: "12px",
                width: "100%",
                height: "110px",
              }}>
                {[
                  { icon: "/Iconos/acciones rapidas/nuevas acciones/ingreso.png", label: "+ingreso", path: "/dashboard/transactions?action=add-income", iconW: 51.08, iconH: 51.08 },
                  { icon: "/Iconos/acciones rapidas/nuevas acciones/gasto.png", label: "+Gasto", path: "/dashboard/transactions?action=add-expense", iconW: 51.08, iconH: 51.08 },
                  { icon: "/Iconos/acciones rapidas/nuevas acciones/agregar tarjeta.png", label: "Agregar tarjeta", path: "/dashboard/credit-cards?action=add", iconW: 51.08, iconH: 51.08 },
                  { icon: "/Iconos/acciones rapidas/nuevas acciones/agregar viaje.png", label: "Nuevo viaje", path: "/dashboard/trips?action=add", iconW: 51.08, iconH: 51.08 },
                ].map(a => (
                  <button key={a.label}
                    onClick={() => navigate(a.path)}
                    className="aorra-quick-action-btn"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "0px",
                      gap: "4px",
                      width: "88px",
                      height: "110px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      fontFamily: "'Manrope', sans-serif",
                    }}
                  >
                    <div style={{
                      position: "relative",
                      width: "83px",
                      height: "83px",
                      borderRadius: "100px",
                      background: "#F0FDFD",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease",
                    }}
                      className="aorra-action-circle"
                    >
                      <img className="aorra-action-img" src={a.icon} alt={a.label} style={{ width: a.iconW, height: a.iconH, objectFit: "contain" }} />
                    </div>
                    <span className="aorra-action-text" style={{
                      width: "100%",
                      height: "19px",
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 700,
                      fontSize: "11px",
                      lineHeight: "19px",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      color: "#0D306A",
                    }}>{a.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
  );

  const financialScoreJSX = (
    <div style={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: "24px 22px",
            gap: "30px",
            width: "100%",
            maxWidth: "544px",
            height: "207px",
            background: "#FFFFFF",
            boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
            borderRadius: "20px",
          }}>
            {/* Left Circle Container with 3D Icon */}
            <div style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "83px",
              height: "83px",
              borderRadius: "100px",
              background: "#F0FDFD",
              flexShrink: 0,
            }}>
              <img
                src="/Iconos/scroe financiero/score fianciero.png"
                alt="Score"
                style={{ width: "42px", height: "66px", objectFit: "contain" }}
              />
            </div>

            {/* Right Details Container */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "space-between",
              padding: "0px",
              gap: "16px",
              width: "100%",
              maxWidth: "352px",
              height: "160px",
            }}>
              {/* Card Title */}
              <h3 style={{
                margin: 0,
                width: "100%",
                height: "30px",
                fontFamily: "'Manrope', sans-serif",
                fontStyle: "normal",
                fontWeight: 700,
                fontSize: "22px",
                lineHeight: "30px",
                color: "#255E99",
              }}>
                Score financiero
              </h3>

              {/* Progress Area */}
              <div style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                padding: "0px",
                gap: "8px",
                width: "100%",
                height: "34.72px",
              }}>
                <div style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  padding: "0px",
                  gap: "8px",
                  width: "100%",
                  height: "21px",
                }}>
                  <span style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 600,
                    fontSize: "18px",
                    lineHeight: "21px",
                    color: "#396BE9",
                  }}>
                    {scoreLabel}
                  </span>
                  <span style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "19px",
                    color: "#255E99",
                  }}>
                    Score {summary?.health_score||0}/100
                  </span>
                </div>

                {/* Customized Progress Bar */}
                <div style={{
                  position: "relative",
                  width: "100%",
                  height: "5.72px",
                  background: "#396BE9",
                  borderRadius: "3px",
                  overflow: "hidden",
                }}>
                  <div style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${summary?.health_score||0}%`,
                    background: "linear-gradient(90deg, #82FFC3 20.67%, #CDF7F7 62%)",
                    borderRadius: "3px",
                    transition: "width 1s ease-in-out",
                  }} />
                </div>
              </div>

              {/* Insight Text */}
              <div style={{
                width: "100%",
                maxHeight: "46px",
                overflow: "hidden",
              }}>
                <p style={{
                  width: "100%",
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 400,
                  fontSize: "13px",
                  lineHeight: "18px",
                  textAlign: "center",
                  color: "#5C5C5C",
                  margin: 0,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                  💡 {loadingInsight ? "Obteniendo diagnóstico IA..." : (aiInsight?.length > 120 ? aiInsight.slice(0, 120) + "…" : aiInsight)}
                </p>
              </div>
            </div>
          </div>
  );

  const creditCardsJSX = (
    <div style={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "0px",
            gap: "16px",
            width: "100%",
            maxWidth: "544px",
            height: "auto",
          }}>
            {/* Title Row */}
            <div style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0px",
              gap: "12px",
              width: "100%",
              height: "33px",
            }}>
              <h3 style={{
                margin: 0,
                width: "100%",
                height: "33px",
                fontFamily: "'Manrope', sans-serif",
                fontStyle: "normal",
                fontWeight: 700,
                fontSize: "22px",
                lineHeight: "33px",
                color: "#255E99",
              }}>
                Tarjetas de crédito
              </h3>
            </div>

            {/* Cards Container */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "0px",
              gap: "16px",
              width: "100%",
            }}>
              {creditCards.slice(0, 3).map((card_item, i) => {
                const isVisa = (card_item.brand || "").toLowerCase().includes("visa");
                const cardBg = isVisa ? "#353535" : "#ADE5F9";
                return (
                  <div key={i} style={{
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    padding: "16px 24px",
                    gap: "5px",
                    width: "100%",
                    height: "135px",
                    background: "#FFFFFF",
                    boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
                    borderRadius: "20px",
                  }}>
                    {/* Top Row: visual card + details */}
                    <div style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      padding: "0px",
                      gap: "12px",
                      width: "100%",
                      height: "72px",
                    }}>
                      {/* Left card box */}
                      <div style={{
                        width: "80px",
                        height: "56px",
                        background: cardBg,
                        borderRadius: "8px",
                        position: "relative",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}>
                        {card_item.icon && typeof card_item.icon === "string" && card_item.icon.startsWith("/") ? (
                          <img src={card_item.icon} alt={card_item.name} style={{ width: 50, height: 34, objectFit: "contain" }} />
                        ) : isVisa ? (
                          // White VISA Text
                          <span style={{
                            color: "#FFFFFF",
                            fontFamily: "'Manrope', sans-serif",
                            fontStyle: "italic",
                            fontWeight: "900",
                            fontSize: "16px",
                            letterSpacing: "-0.05em",
                          }}>VISA</span>
                        ) : (
                          // Mastercard Overlapping Circles Logo
                          <svg width="28" height="16" viewBox="0 0 30 18">
                            <circle cx="9" cy="9" r="9" fill="#EB001B" />
                            <circle cx="21" cy="9" r="9" fill="#F79E1B" fillOpacity="0.85" />
                          </svg>
                        )}
                      </div>

                      {/* Right details frame */}
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        padding: "0px",
                        gap: "8px",
                        flex: 1,
                        height: "auto",
                      }}>
                        {/* Brand & Number & Cupo */}
                        <div style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}>
                          {/* Name */}
                          <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <span style={{
                              fontFamily: "'Manrope', sans-serif",
                              fontWeight: 700,
                              fontSize: "16px",
                              lineHeight: "24px",
                              color: "#353535",
                              textTransform: "capitalize",
                            }}>
                              {card_item.bank || card_item.name || card_item.brand || "Tarjeta"}
                            </span>
                          </div>

                          {/* Cupo */}
                          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "6px", marginLeft: "12px", flexShrink: 0 }}>
                            <span style={{
                              fontFamily: "'Manrope', sans-serif",
                              fontWeight: 700,
                              fontSize: "13px",
                              color: "#353535",
                            }}>
                              Cupo total
                            </span>
                            <span style={{
                              fontFamily: "'Manrope', sans-serif",
                              fontWeight: 400,
                              fontSize: "13px",
                              color: "#353535",
                            }}>
                              {fmt(card_item.credit_limit || 0, currency)}
                            </span>
                          </div>
                        </div>

                        {/* Separator line */}
                        <div style={{
                          width: "100%",
                          borderTop: "1px solid #DDDDDD",
                        }} />

                        {/* Bottom Row: Dates + Deuda */}
                        <div style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}>
                          {/* Dates (Left) */}
                          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                            {card_item.payment_day && (
                              <span style={{
                                fontFamily: "'Manrope', sans-serif",
                                fontWeight: 700,
                                fontSize: "13px",
                                color: "#353535",
                              }}>
                                Paga el: <span style={{ fontWeight: 400 }}>{card_item.payment_day}</span>
                              </span>
                            )}
                          </div>

                          {/* Deuda (Right) */}
                          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "6px" }}>
                            <span style={{
                              fontFamily: "'Manrope', sans-serif",
                              fontWeight: 700,
                              fontSize: "13px",
                              color: "#353535",
                            }}>
                              Deuda a la fecha
                            </span>
                            <span style={{
                              fontFamily: "'Manrope', sans-serif",
                              fontWeight: 400,
                              fontSize: "13px",
                              color: "#353535",
                            }}>
                              {fmt(card_item.current_debt || card_item.current_balance || 0, currency)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {creditCards.length === 0 && (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "135px",
                  background: "#FFFFFF",
                  borderRadius: "20px",
                  boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
                  gap: "12px",
                }}>
                  <p style={{ color: "#aaa", fontSize: 14, fontFamily: "'Manrope', sans-serif", margin: 0 }}>
                    No hay tarjetas registradas
                  </p>
                  <button
                    onClick={() => navigate("/dashboard/credit-cards?tab=cards")}
                    style={{
                      background: "#E1E9FC",
                      color: "#396BE9",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "12px",
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 700,
                      fontSize: "13px",
                      cursor: "pointer",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = 0.8}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = 1}
                  >
                    + Agregar tarjeta
                  </button>
                </div>
              )}
            </div>

            {/* Pay Button */}
            <button
              onClick={() => navigate("/dashboard/credit-cards?tab=cards")}
              className="aorra-pay-button"
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                padding: "12px",
                width: "100%",
                height: "46px",
                background: "#E1E9FC",
                borderRadius: "20px",
                border: "none",
                cursor: "pointer",
                transition: "transform 0.2s ease, background-color 0.2s ease",
              }}
            >
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "22px",
                color: "#396BE9",
              }}>
                Pagar
              </span>
            </button>
          </div>
  );

  const recentTransactionsJSX = (
    <div style={{
            ...card,
            padding: "30px 25px",
            gap: 12,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}>
            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width: "100%" }}>
              <h3 style={{
                fontFamily:"'Manrope', sans-serif",
                fontWeight:700,
                fontSize:"22px",
                lineHeight:"33px",
                color:"#255E99",
                margin:0,
              }}>Movimientos</h3>
              <button
                onClick={() => navigate("/dashboard/transactions")}
                style={{
                  fontFamily:"'Manrope', sans-serif",
                  fontWeight:700,
                  fontSize:16,
                  lineHeight:"22px",
                  color:"#396BE9",
                  background:"none",
                  border:"none",
                  cursor:"pointer",
                  padding:0,
                }}
              >
                Ver todos
              </button>
            </div>

            {/* Transaction rows */}
            <div style={{ display:"flex", flexDirection:"column", width:"100%" }}>
              {transactions.slice(0,5).map((tx, i) => (
                <div key={tx.id||i} style={{
                  display:"flex",
                  flexDirection:"row",
                  justifyContent:"space-between",
                  alignItems:"center",
                  padding:"18px 0",
                  gap:12,
                  borderBottom: i < Math.min(transactions.length, 5) - 1 ? "1px solid #E0EDF8" : "none",
                }}>
                  {/* Left: Icon + Name */}
                  <div style={{ display:"flex", alignItems:"center", gap:16, flex:1, minWidth:0 }}>
                    <div style={{
                      width:48,
                      height:48,
                      borderRadius:"50%",
                      background:"#F6F8FD",
                      display:"flex",
                      alignItems:"center",
                      justifyContent:"center",
                      flexShrink:0,
                    }}>
                      {getCatIcon(null, tx.category, 26)}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0, gap: 3 }}>
                      <span style={{
                        fontFamily:"'Manrope', sans-serif",
                        fontWeight:400,
                        fontSize:18,
                        lineHeight:"25px",
                        color:"#000000",
                        overflow:"hidden",
                        textOverflow:"ellipsis",
                        whiteSpace:"nowrap",
                      }}>
                        {tx.category || "Sin categoría"}
                      </span>
                      {isCreditCardExpense(tx) && (
                        <span style={{
                          width: "fit-content",
                          padding: "2px 7px",
                          borderRadius: 999,
                          background: "#EEF4FF",
                          color: "#396BE9",
                          fontSize: 10,
                          lineHeight: "14px",
                          fontWeight: 800,
                        }}>
                          💳 {getCreditCardName(tx)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: Date + Amount */}
                  <div style={{
                    display:"flex",
                    flexDirection:"column",
                    alignItems:"flex-end",
                    gap:4,
                    flexShrink:0,
                  }}>
                    <span style={{
                      fontFamily:"'Quicksand', 'Manrope', sans-serif",
                      fontWeight:400,
                      fontSize:12,
                      lineHeight:"15px",
                      color:"#8E8E8E",
                    }}>
                      {fmtDate(tx.created_at||tx.date)}
                    </span>
                    <span style={{
                      fontFamily:"'Manrope', sans-serif",
                      fontWeight:700,
                      fontSize:16,
                      lineHeight:"22px",
                      color:"#255E99",
                    }}>
                      {tx.type==="income" ? "+" : "- "}{fmt(Math.abs(tx.amount||0), currency)}
                    </span>
                  </div>
                </div>
              ))}
              {transactions.length === 0 && (
                <EmptyState title="Sin resultados" subtitle="No hay movimientos registrados" />
              )}
            </div>
          </div>
  );

  return (
    <div data-testid="dashboard-page" style={{ fontFamily:"'Manrope', 'Inter', sans-serif" }}>

      {isMobile ? (
        <div style={{ display:"flex", flexDirection:"column", gap:24, paddingTop: 8 }}>
          {/* 1- mi dashboard + filtro de fecha */}
          {headerRowJSX}
          {/* 2- 3 tarjetas en 2 filas */}
          {kpiCardsJSX}
          {/* 3- Categorias (ahora debajo de las tarjetas) */}
          {categoriesJSX}
          {/* 4- grafico de estadisticas */}
          {chartCardJSX}
          {/* 5- accesos rapidos */}
          {fastAccessJSX}
          {/* 6- score financiero */}
          {financialScoreJSX}
          {/* 7- proximos pagos */}
          {upcomingPaymentsJSX}
          {/* 8- Mis tarjetas */}
          {creditCardsJSX}
          {/* 9- ultimos movimientos */}
          {recentTransactionsJSX}
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 410px", gap:24, alignItems:"start", paddingTop: 8 }}>
          {/* ──────── LEFT COLUMN ──────── */}
          <div style={{ display:"flex", flexDirection:"column", gap:24, minWidth: 0 }}>
            {headerRowJSX}
            {kpiCardsJSX}
            {categoriesJSX}
            {chartCardJSX}
            {upcomingPaymentsJSX}
          </div>
          {/* ──────── RIGHT COLUMN ──────── */}
          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
            {fastAccessJSX}
            {financialScoreJSX}
            {creditCardsJSX}
            {recentTransactionsJSX}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          CATEGORY DIALOG
      ══════════════════════════════════════════════ */}
      {categoryDialogOpen && (
        <div
          onClick={closeCategoryDialog}
          style={{ position:"fixed", inset:0, zIndex:100, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.35)", backdropFilter:"blur(4px)", padding: "16px" }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              display:"flex", flexDirection:"column", alignItems:"center",
              padding:"20px 20px", gap:10,
              width:"90%", maxWidth:380, maxHeight: "88vh", overflowY: "auto",
              background:"#FFFFFF",
              boxShadow:"-2px 0px 20px rgba(176, 179, 189, 0.25)",
              borderRadius:20,
              position:"relative",
              boxSizing:"border-box"
            }}
          >
            {/* Close X */}
            <button
              onClick={closeCategoryDialog}
              style={{
                position:"absolute", top:12, right:12,
                width:24, height:24, background:"none", border:"none", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}
            >
              <XIcon size={18} strokeWidth={2} color="#255E99" />
            </button>

            {/* Title */}
            <h2 style={{
              fontFamily:"'Manrope', sans-serif", fontWeight:700, fontSize:18, lineHeight:"24px",
              color:"#1B2559", margin:0, textAlign:"center",
            }}>
              {categoryDeleteFlow ? "Eliminar categoría" : ((editingCategoryId || editingCategoryOriginalName) ? "Editar categoría" : "Nueva categoría")}
            </h2>

            {/* Icon Preview Circle */}
            <div style={{
              width:54, height:54, borderRadius:"100px",
              border:"1px dashed #4A4A4A",
              display:"flex", alignItems:"center", justifyContent:"center",
              background:"#FFFFFF", overflow:"hidden", flexShrink:0,
            }}>
              {categoryForm.icon && categoryForm.icon.startsWith("/") ? (
                <img src={categoryForm.icon} alt="" style={{ width:38, height:38, objectFit:"contain" }} />
              ) : (
                <span style={{ fontSize:28 }}>{categoryForm.icon || "⭐"}</span>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleSaveCategory} style={{ display: categoryDeleteFlow ? "none" : "flex", flexDirection:"column", gap:10, width:"100%" }}>
              {/* Nombre */}
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                <label style={{ fontFamily:"'Manrope', sans-serif", fontWeight:800, fontSize:13, color:"#5C5C5C" }}>
                  Nombre
                </label>
                <input
                  placeholder="Ej: Transporte"
                  value={categoryForm.name}
                  onChange={e => setCategoryForm({...categoryForm, name:e.target.value})}
                  style={{
                    boxSizing:"border-box", width:"100%", height:36, padding:"6px 10px",
                    border:"1px solid #DDDDDD", borderRadius:12,
                    fontFamily:"'Manrope', sans-serif", fontWeight:400, fontSize:13, color:"#4A4A4A",
                    outline:"none",
                  }}
                />
              </div>

              {/* Presupuesto */}
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                <label style={{ fontFamily:"'Manrope', sans-serif", fontWeight:800, fontSize:13, color:"#5C5C5C" }}>
                  Presupuesto mensual <span style={{ fontWeight:400 }}>(opcional)</span>
                </label>
                <input
                  type="number"
                  placeholder="$90.000"
                  value={categoryForm.budget}
                  onChange={e => setCategoryForm({...categoryForm, budget:e.target.value})}
                  style={{
                    boxSizing:"border-box", width:"100%", height:36, padding:"6px 10px",
                    border:"1px solid #DDDDDD", borderRadius:12,
                    fontFamily:"'Manrope', sans-serif", fontWeight:400, fontSize:13, color:"#4A4A4A",
                    outline:"none",
                  }}
                />
              </div>

              {/* Icon Selector (Grid 6 cols) */}
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label style={{ fontFamily:"'Manrope', sans-serif", fontWeight:800, fontSize:13, color:"#5C5C5C" }}>
                  Selecciona un icono
                </label>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(6, 1fr)", gap:6 }}>
                  {[
                    { src:"/Iconos/Categorias/Alimnetacion.png", name:"Alimentación" },
                    { src:"/Iconos/Categorias/Suscripciones.png", name:"Suscripciones" },
                    { src:"/Iconos/Categorias/Ahorros.png", name:"Ahorros" },
                    { src:"/Iconos/Categorias/Deudas.png", name:"Deudas" },
                    { src:"/Iconos/Categorias/salud.png", name:"Salud" },
                    { src:"/Iconos/Categorias/Entretenimiento.png", name:"Entretenimiento" },
                    { src:"/Iconos/Categorias/otros.png", name:"Otros" },
                    { src:"/Iconos/Categorias/Servicios.png", name:"Servicios" },
                    { src:"/Iconos/Categorias/Hogar.png", name:"Hogar" },
                    { src:"/Iconos/Categorias/transporte.png", name:"Transporte" },
                    { src:"/Iconos/Categorias/Viajes.png", name:"Viajes" },
                    { src:"/Iconos/Categorias/deporte.png", name:"Deportes" },
                  ].map(iconOption => {
                    const isSelected = categoryForm.icon === iconOption.src;
                    return (
                      <button
                        key={iconOption.src}
                        type="button"
                        onClick={() => setCategoryForm({...categoryForm, icon: iconOption.src})}
                        style={{
                          boxSizing:"border-box", position:"relative",
                          width:"100%", aspectRatio:"1",
                          display:"flex", alignItems:"center", justifyContent:"center",
                          padding:4,
                          background:"rgba(225, 233, 252, 0.73)",
                          border: isSelected ? "2px solid #396BE9" : "1px solid rgba(225, 233, 252, 0.73)",
                          borderRadius:12, cursor:"pointer",
                          transition:"all 0.15s ease",
                        }}
                      >
                        <img src={iconOption.src} alt={iconOption.name} style={{ width:"75%", height:"75%", objectFit:"contain" }} />
                        {isSelected && (
                          <div style={{
                            position:"absolute", top:-2, right:-2,
                            width:14, height:14, borderRadius:"50%",
                            background:"#C7FF65", display:"flex", alignItems:"center", justifyContent:"center",
                            boxShadow:"0 1px 3px rgba(0,0,0,0.15)",
                          }}>
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#141B34" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Color Selector (1 single row of 8 colors) */}
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                <label style={{ fontFamily:"'Manrope', sans-serif", fontWeight:800, fontSize:13, color:"#5C5C5C" }}>
                  Color de la barra / gráfico
                </label>
                <ColorPalette value={categoryForm.color} onChange={color => setCategoryForm({ ...categoryForm, color })} />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={categorySubmitting}
                style={{
                  width:"100%", height:42,
                  display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                  padding:"0 20px", marginTop:4,
                  background:"#396BE9", borderRadius:12, border:"none",
                  fontFamily:"'Manrope', sans-serif", fontWeight:600, fontSize:14,
                  color:"#FFFFFF", cursor:"pointer",
                  transition:"opacity 0.2s",
                  opacity: categorySubmitting ? 0.7 : 1,
                }}
              >
                {categorySubmitting && <Loader2 size={16} style={{ animation:"spin 1s linear infinite" }} />}
                Guardar cambios
              </button>

              {/* Delete Button text link inside modal if editing */}
              {(editingCategoryId || editingCategoryOriginalName) && (
                <button
                  type="button"
                  onClick={handleDeleteCategory}
                  disabled={categoryDeleting}
                  style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#FF0040",
                    cursor: "pointer",
                    marginTop: 2,
                    textAlign: "center",
                    textDecoration: "underline",
                    opacity: categoryDeleting ? 0.65 : 1,
                  }}
                >
                  {categoryDeleting ? "Revisando categoría…" : "Eliminar categoría"}
                </button>
              )}
            </form>

            {categoryDeleteFlow && (
              <div style={{ display:"flex", flexDirection:"column", gap:12, width:"100%", fontFamily:"'Manrope', sans-serif" }}>
                <div style={{ padding:"12px 14px", borderRadius:14, background:"#FFF5F6", border:"1px solid #FFD7DE", color:"#5C5C5C", fontSize:13, lineHeight:"19px" }}>
                  La categoría <strong style={{ color:"#1B2559" }}>{categoryDeleteFlow.preview.category}</strong> tiene{" "}
                  <strong>{categoryDeleteFlow.preview.transaction_count} movimiento{categoryDeleteFlow.preview.transaction_count === 1 ? "" : "s"}</strong>.
                  Elige qué quieres hacer antes de eliminarla.
                </div>

                {categoryDeleteFlow.step === "choice" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setCategoryDeleteFlow({ ...categoryDeleteFlow, step:"move", error:"" })}
                      style={{ width:"100%", minHeight:48, padding:"10px 14px", borderRadius:14, border:"1px solid #BDD0FF", background:"#F4F7FF", color:"#255E99", fontWeight:700, fontSize:13, cursor:"pointer", textAlign:"left" }}
                    >
                      Mover movimientos
                      <span style={{ display:"block", marginTop:2, color:"#6B7280", fontWeight:400, fontSize:12 }}>Conserva el historial en otra categoría.</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => confirmCategoryDeletion("delete_movements")}
                      disabled={categoryDeleting}
                      style={{ width:"100%", minHeight:48, padding:"10px 14px", borderRadius:14, border:"1px solid #FFC2CC", background:"#FFF5F6", color:"#D90429", fontWeight:700, fontSize:13, cursor:"pointer", textAlign:"left", opacity:categoryDeleting ? 0.65 : 1 }}
                    >
                      Eliminar movimientos totalmente
                      <span style={{ display:"block", marginTop:2, color:"#6B7280", fontWeight:400, fontSize:12 }}>Esta acción elimina definitivamente los movimientos.</span>
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                      <label style={{ fontWeight:800, fontSize:13, color:"#5C5C5C" }}>Mover movimientos a</label>
                      <select
                        value={categoryDeleteFlow.destination}
                        onChange={(event) => setCategoryDeleteFlow({ ...categoryDeleteFlow, destination:event.target.value, error:"" })}
                        style={{ boxSizing:"border-box", width:"100%", height:42, padding:"6px 10px", border:"1px solid #DDDDDD", borderRadius:12, background:"#FFFFFF", color:categoryDeleteFlow.destination ? "#4A4A4A" : "#8F9BBA", fontFamily:"'Manrope', sans-serif", fontSize:13, outline:"none" }}
                      >
                        <option value="">Selecciona una categoría</option>
                        {categoryDeleteFlow.preview.available_categories.map((category) => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => confirmCategoryDeletion("move")}
                      disabled={categoryDeleting}
                      style={{ width:"100%", height:42, borderRadius:12, border:"none", background:"#396BE9", color:"#FFFFFF", fontWeight:700, fontSize:13, cursor:"pointer", opacity:categoryDeleting ? 0.65 : 1 }}
                    >
                      {categoryDeleting ? "Moviendo…" : "Mover y eliminar categoría"}
                    </button>
                    <button type="button" onClick={() => setCategoryDeleteFlow({ ...categoryDeleteFlow, step:"choice", error:"" })} style={{ border:"none", background:"none", color:"#396BE9", fontWeight:600, fontSize:13, cursor:"pointer" }}>
                      Volver
                    </button>
                  </>
                )}

                {categoryDeleteFlow.error && (
                  <p style={{ margin:0, padding:"8px 10px", borderRadius:10, background:"#FFF5F6", color:"#D90429", fontSize:12 }}>{categoryDeleteFlow.error}</p>
                )}
                <button type="button" onClick={closeCategoryDialog} style={{ border:"none", background:"none", color:"#6B7280", fontWeight:600, fontSize:13, cursor:"pointer" }}>
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </div>
      )}



      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        
        /* ─── Calendar Selected / Highlight Overrides (Orange to Blue) ─── */
        
        /* Today highlight (when not selected) */
        .rdp-day_today,
        .day-today,
        .bg-accent {
          background-color: #E1E9FC !important;
          color: #255E99 !important;
          border-radius: 50% !important;
        }

        /* Range middle highlight connector (was orange) */
        .rdp-day_range_middle,
        .aria-selected\:bg-accent,
        [aria-selected="true"].day-range-middle {
          background-color: #E1E9FC !important;
          color: #255E99 !important;
          border-radius: 0px !important;
        }

        /* Selected individual days, range start, and range end */
        .rdp-day_selected,
        .day-selected,
        .rdp-day_range_start,
        .rdp-day_range_end,
        .day-range-start,
        .day-range-end {
          background-color: #255E99 !important;
          color: #ffffff !important;
          border-radius: 50% !important;
        }

        /* Table cell connectors wrapper background highlight override (was orange accent) */
        .rdp-cell:has([aria-selected="true"]),
        td:has([aria-selected="true"]),
        tr:has([aria-selected="true"]) td {
          background-color: #E1E9FC !important;
        }

        /* Keep start and end day cell corners rounded */
        td:has(.rdp-day_range_start),
        td:has(.day-range-start) {
          border-top-left-radius: 50% !important;
          border-bottom-left-radius: 50% !important;
        }
        td:has(.rdp-day_range_end),
        td:has(.day-range-end) {
          border-top-right-radius: 50% !important;
          border-bottom-right-radius: 50% !important;
        }

        /* Day hover style */
        .rdp-day:hover {
          background-color: #255E99 !important;
          color: #ffffff !important;
          border-radius: 50% !important;
        }

        /* KPI Card Hover Grow Animation */
        .aorra-kpi-card {
          transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease !important;
        }
        
        .aorra-kpi-card:hover {
          transform: translateY(-4px) scale(1.02);
          box-shadow: -2px 10px 24px rgba(176, 179, 189, 0.35) !important;
        }

        .aorra-payment-carousel {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 16px;
          width: 100%;
          overflow-x: auto;
          scroll-behavior: smooth;
        }
        .aorra-payment-carousel::-webkit-scrollbar,
        .aorra-category-chips::-webkit-scrollbar {
          display: none !important;
        }

        .aorra-payment-card {
          transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease !important;
          flex: 0 0 calc((100% - 32px) / 2.3) !important;
          min-width: calc((100% - 32px) / 2.3) !important;
        }
        .aorra-payment-card:hover {
          transform: translateY(-4px);
          box-shadow: -2px 10px 30.4px rgba(176, 179, 189, 0.22) !important;
        }

        .aorra-carousel-nav:hover {
          background-color: #396BE9 !important;
          color: #FFFFFF !important;
          transform: scale(1.08);
        }

        .aorra-whatsapp-switch[data-state="checked"] {
          background-color: #62BF92 !important;
        }

        .aorra-quick-action-btn:hover .aorra-action-circle {
          transform: scale(1.08);
          background-color: #E1E9FC !important;
        }

        .aorra-pay-button {
          transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease !important;
        }
        .aorra-pay-button:hover {
          background-color: #396BE9 !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(57,107,233,0.25);
        }
        .aorra-pay-button span {
          transition: color 0.2s ease;
        }
        .aorra-pay-button:hover span {
          color: #FFFFFF !important;
        }

        .aorra-chart-header {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          justify-content: space-between !important;
          gap: 12px !important;
        }

        .aorra-chart-toggle {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          padding: 4px !important;
          width: 277px !important;
          height: 48px !important;
          background: #FFFFFF !important;
          box-shadow: -2px 0px 20px rgba(176, 179, 189, 0.25) !important;
          border-radius: 16px !important;
          box-sizing: border-box !important;
        }

        .aorra-chart-toggle-btn {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          flex: 1 !important;
          height: 40px !important;
          border-radius: 16px !important;
          border: none !important;
          font-family: 'Manrope', sans-serif !important;
          font-weight: 700 !important;
          font-size: 12px !important;
          cursor: pointer !important;
          transition: background-color 0.2s, color 0.2s !important;
        }

        @keyframes chartPulse {
          from { opacity: 0.3; transform: scale(0.85); }
          to   { opacity: 1;   transform: scale(1.15); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }

        .aorra-kpi-card-container {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
          width: 100%;
        }
        .aorra-kpi-card-balance {
          grid-column: span 1;
        }

        .aorra-dashboard-heading {
          display: grid;
          grid-template-columns: auto auto minmax(0, 1fr);
          grid-template-areas: "title filter cash";
          align-items: center;
          column-gap: 24px;
          width: 100%;
          min-height: 49px;
        }
        .aorra-dashboard-title { grid-area: title; }
        .aorra-date-filter { grid-area: filter; }
        .aorra-cash-summary {
          grid-area: cash;
          justify-self: end;
          display: flex;
          align-items: baseline;
          justify-content: flex-end;
          gap: 0 10px;
          min-width: 0;
          font-family: 'Manrope', sans-serif;
          color: #255E99;
          text-align: right;
        }
        .aorra-cash-summary span {
          font-size: 16px;
          font-weight: 700;
          color: #5F6F8F;
          white-space: nowrap;
        }
        .aorra-cash-summary strong {
          font-size: 29px;
          font-weight: 800;
          line-height: 34px;
          letter-spacing: -0.7px;
          white-space: nowrap;
        }

        @media (max-width:900px) {
          .aorra-dashboard-heading {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            grid-template-areas:
              "title cash"
              "filter cash";
            row-gap: 14px;
            column-gap: 12px;
            align-items: center;
          }
          .aorra-dashboard-title {
            font-size: 22px !important;
            white-space: nowrap;
          }
          .aorra-date-filter {
            justify-self: start;
          }
          .aorra-cash-summary {
            justify-self: center;
            align-self: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            gap: 2px;
            width: auto;
          }
          .aorra-cash-summary span {
            order: 2;
            font-size: 16px;
            line-height: 20px;
          }
          .aorra-cash-summary strong {
            order: 1;
            font-size: 29px;
            line-height: 34px;
            letter-spacing: -0.7px;
          }
          .aorra-kpi-card-container {
            display: grid !important;
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px !important;
            padding-bottom: 0px !important;
          }
          .aorra-kpi-card-container::-webkit-scrollbar {
            display: none !important;
          }
          .aorra-kpi-card {
            flex: unset !important;
            min-width: 0 !important;
            height: auto !important;
            min-height: 140px !important;
            padding: 12px !important;
          }
          .aorra-kpi-card-balance {
            grid-column: span 2 !important;
            min-height: 80px !important;
          }
          .aorra-kpi-card-balance .aorra-kpi-inner-container {
            flex-direction: row !important;
            align-items: center !important;
            gap: 16px !important;
          }
          .aorra-kpi-card-balance .aorra-kpi-text-block {
            margin-top: 0 !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: space-between !important;
            flex: 1 !important;
          }
          .aorra-kpi-card-balance .aorra-kpi-text-titles {
            gap: 2px !important;
          }
          .aorra-kpi-card-balance .aorra-kpi-icon-circle {
            width: 54px !important;
            height: 54px !important;
          }
          .aorra-kpi-card-balance .aorra-kpi-icon-circle img {
            width: 34px !important;
            height: 34px !important;
          }
          .aorra-kpi-card-balance .aorra-kpi-value {
            font-size: 20px !important;
          }
          .aorra-kpi-card-balance .aorra-kpi-label {
            font-size: 14px !important;
          }
          .aorra-kpi-card .aorra-kpi-value {
            font-size: 18px !important;
            line-height: 24px !important;
          }
          .aorra-kpi-card .aorra-kpi-label {
            font-size: 13px !important;
            line-height: 18px !important;
          }
          .aorra-kpi-icon-circle {
            width: 48px !important;
            height: 48px !important;
          }
          .aorra-kpi-icon-circle img {
            width: 30px !important;
            height: 30px !important;
          }
          

          /* --- CHART HEADER --- */
          .aorra-chart-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            justify-content: flex-start !important;
            gap: 10px !important;
            flex-wrap: nowrap !important;
          }
          .aorra-chart-header h3 {
            font-size: 20px !important;
            line-height: 28px !important;
            width: 100% !important;
          }
          .aorra-chart-toggle {
            width: 100% !important;
            max-width: 240px !important;
            height: 36px !important;
            flex-shrink: 0 !important;
          }
          .aorra-chart-toggle-btn {
            height: 28px !important;
            font-size: 11px !important;
          }
          .aorra-chart-tabs {
            justify-content: flex-start !important;
            gap: 0 !important;
            padding-left: 0 !important;
          }
          .aorra-chart-tabs button {
            padding: 6px 8px !important;
            font-size: 11px !important;
            flex-shrink: 0 !important;
          }
          .aorra-payment-card {
            flex: 0 0 calc((100% - 16px) / 1.3) !important;
            min-width: calc((100% - 16px) / 1.3) !important;
          }

          /* --- QUICK ACTIONS: two clear rows on mobile --- */
          .aorra-quick-actions-card {
            height: auto !important;
            min-height: 370px !important;
            padding: 26px 22px !important;
          }
          .aorra-quick-actions-content {
            height: auto !important;
            gap: 18px !important;
          }
          .aorra-quick-actions-container {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 20px 16px !important;
            justify-items: center !important;
            height: auto !important;
          }
          .aorra-quick-action-btn {
            width: 100% !important;
            height: 126px !important;
            gap: 8px !important;
          }
          .aorra-action-circle {
            width: 82px !important;
            height: 82px !important;
          }
          .aorra-action-img {
            width: 48px !important;
            height: 48px !important;
          }
          .aorra-action-text {
            height: auto !important;
            font-size: 14px !important;
            line-height: 19px !important;
            white-space: normal !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DashboardPage;
